import { useEffect } from "react"

import { useSearchParams } from "wouter"

import { CATEGORY_SEARCH_PARAM_NAME, LENGTH_SEARCH_PARAM_NAME } from "@/config"
import { useSkipassStore } from "@/store/skipassStore"

import { useCategoryParam, useStepParam } from "./hooks"
import useSeedUrlDefaults from "./hooks/useSeedUrlDefaults"

type FlowStep = "search" | "details"
type SearchView = "start" | "selectPass"

const useSkipassFlow = () => {
  const [, setSearchParams] = useSearchParams()
  const { step, goToDetails, goBackToSearch } = useStepParam()
  const { categoryId, setCategory } = useCategoryParam()
  useSeedUrlDefaults()
  const selectedProducts = useSkipassStore((store) => store.selectedProducts)
  const clearSelectionState = useSkipassStore(
    (store) => store.clearSelectionState
  )

  // Clear selection state when category is not in URL (initial load or browser back)
  useEffect(() => {
    if (categoryId === undefined) {
      clearSelectionState()
    }
  }, [categoryId, clearSelectionState])

  // Validate: Details page requires products, otherwise redirect to search
  useEffect(() => {
    if (step === "details" && selectedProducts.length === 0) {
      goBackToSearch()
    }
  }, [step, selectedProducts, goBackToSearch])

  // Derive flowStep from URL step param
  const flowStep: FlowStep = step === "details" ? "details" : "search"

  const searchView: SearchView =
    categoryId !== undefined ? "selectPass" : "start"

  const selectCategory = (selectedCategoryId: number) => {
    setCategory(selectedCategoryId)
  }

  const returnToStartView = () => {
    setSearchParams(
      (prev) => {
        prev.delete(CATEGORY_SEARCH_PARAM_NAME)
        prev.delete(LENGTH_SEARCH_PARAM_NAME)
        return prev
      },
      { replace: false }
    )
    clearSelectionState()
  }

  const proceedToDetailsStep = () => {
    goToDetails()
  }

  const returnToSearchStep = () => {
    goBackToSearch()
  }

  return {
    flowStep,
    searchView,
    actions: {
      selectCategory,
      returnToStartView,
      proceedToDetailsStep,
      returnToSearchStep,
    },
  }
}

export default useSkipassFlow
